import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Barcode } from "@phosphor-icons/react";
import { logger } from "../lib/logger";

export default function BarcodeScanner({ open, onClose, onDetected }) {
  const [error, setError] = useState("");
  const scannerRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setError("");

    const elId = "barcode-reader";
    let scanner;
    const start = async () => {
      try {
        scanner = new Html5Qrcode(elId, { verbose: false });
        scannerRef.current = scanner;
        startedRef.current = true;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 280, height: 160 } },
          (decodedText) => {
            onDetected?.(decodedText);
            stop();
          },
          () => {}
        );
      } catch (err) {
        logger.error("Barcode scanner start failed:", err);
        setError("No se pudo acceder a la cámara. Verifica permisos.");
      }
    };

    const stop = async () => {
      if (!startedRef.current) return;
      try {
        await scanner?.stop();
        await scanner?.clear();
      } catch (err) {
        logger.error("Barcode scanner stop failed:", err);
      }
      startedRef.current = false;
    };

    start();
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = async () => {
    if (scannerRef.current && startedRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (err) {
        logger.error("Barcode scanner cleanup failed:", err);
      }
      startedRef.current = false;
    }
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="barcode-scanner-modal">
      <div className="glass-card rounded-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Barcode size={22} className="text-turquoise" />
            Escanear código
          </h3>
          <button onClick={handleClose} data-testid="scanner-close-btn" className="text-zinc-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="relative bg-black rounded-lg overflow-hidden aspect-video border border-white/10">
          <div id="barcode-reader" className="w-full h-full" />
          <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 h-[2px] bg-turquoise animate-pulse pointer-events-none shadow-[0_0_8px_#00FFD4]" />
          <div className="absolute inset-8 border-2 border-turquoise/40 rounded-md pointer-events-none" />
        </div>

        {error && <p className="text-sm text-red-400 mt-4 text-center font-mono">{error}</p>}
        <p className="text-xs text-zinc-500 mt-4 text-center font-mono">
          Apunta a un código de barras o QR
        </p>
      </div>
    </div>
  );
}
